import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import NotificationService from "../api/services/notification.service";
import { getCookie } from "../utils/cookies";

interface NotificationContextType {
  unreadCount: number;
  refreshCount: () => void;
  decrementCount: () => void;
  resetCount: () => void;
}

const NotificationContext = createContext<NotificationContextType>({
  unreadCount: 0,
  refreshCount: () => {},
  decrementCount: () => {},
  resetCount: () => {},
});

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const wsRef = useRef<WebSocket | null>(null);

  const refreshCount = async () => {
    try {
      const res = await NotificationService.getNotificationCount();
      setUnreadCount(res.data?.count || 0);
    } catch (e) {
      console.error("Failed to fetch notification count", e);
    }
  };

  const decrementCount = () => {
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const resetCount = () => {
    setUnreadCount(0);
  };

  useEffect(() => {
    // Initial fetch
    refreshCount();

    // Connect to WebSocket for real-time updates
    const WS_BASE = process.env.REACT_APP_WS_API_BASE_URL;
    const token = getCookie("LuxMartAdminDashboardAccessToken");

    if (!token || !WS_BASE) return;

    const createSocket = () => {
      const ws = new WebSocket(
        `${WS_BASE}/chat/ws/admin/?token=${encodeURIComponent(token)}`,
      );
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("Notification WebSocket connected");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Listen for message events from stores
          if (
            data.type === "message" &&
            data.payload?.message?.sender_type === "store"
          ) {
            // Only create notification if NOT on messages page
            // Check current location at the time of receiving the message
            const isOnMessagesPage =
              window.location.pathname.includes("/messages");
            if (!isOnMessagesPage) {
              setUnreadCount((prev) => prev + 1);
            }
          }
          // Also handle explicit notification events
          if (data.type === "new_notification") {
            const isOnMessagesPage =
              window.location.pathname.includes("/messages");
            if (!isOnMessagesPage) {
              setUnreadCount((prev) => prev + 1);
            }
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message", e);
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        // Reconnect after 2 seconds
        setTimeout(() => {
          createSocket();
        }, 2000);
      };

      ws.onerror = (error) => {
        console.error("Notification WebSocket error:", error);
        ws.close();
      };
    };

    createSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []); // Only run once on mount

  const value: NotificationContextType = {
    unreadCount,
    refreshCount,
    decrementCount,
    resetCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
